import { Color } from '@persona-id/design-tokens';
import { startCase, sumBy } from 'lodash/fp';
import { ComponentProps, FC, memo, ReactNode, useCallback, useContext, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Payload } from 'recharts/types/component/DefaultLegendContent';
import { BaseAxisProps, DataKey } from 'recharts/types/util/types';
import { ThemeContext } from 'styled-components';

import ChartLayout, {
  ChartLegendContent,
  ChartTooltipContainer,
  ChartTooltipDivider,
  ChartTooltipHeader,
  ChartTooltipLabel,
  ChartTooltipLabelContainer,
} from 'dashboard/layouts/ChartLayout';
import { formatNumber, formatNumberLarge } from 'dashboard/lib/utils/formatters';
import { BASE } from 'design-system/variables/spacing';
import { TransitionDuration } from 'design-system/variables/transitions';
import Dot from 'images/dashboard/dot.svg';
import useToggle from 'lib/hooks/useToggle';

export interface RenderLabelProps {
  x: number;
  y: number;
  fill: string;
  value: unknown;
  index: number;
  width: number;
  height: number;
}

export interface BarChartWithLegendProps {
  data: Record<any, any>[];
  barKeys: string[];
  dataKeys?: DataKey<any>[];
  barColors: string[];
  stackId: string;
  // need chartWidth for testing - https://stackoverflow.com/questions/60215153/when-mounted-in-enzyme-why-does-a-recharts-chart-with-a-responsivecontainer-not
  chartWidth?: number;

  createTooltipHeader: (label: string | number) => string;
  legendFormatter?: (value: string) => string;
  onDeepLink?: (datum: Record<any, any>, keys: string[]) => void;
  renderTooltip?: (hoveredKey: string) => ReactNode;
  legendHeight?: number | 'auto';
  barKeyFormatter?: (value: string) => string;
  hideLegend?: boolean;
  renderBarLabel?: (props: RenderLabelProps, idx: number) => ReactNode;
  chartHeight?: number;

  // x-axis options
  xAxisHeight?: number;
  xAxisTextAnchor?: string;
  xAxisAngle?: number;
  xDataKey: string | ((data: any) => string);
  xFormatter?: (value: any, index?: number) => string;
  xAxisTick?: BaseAxisProps['tick'];

  // y-axis options
  yAxisUnit?: number | string;
  yAxisTicks?: (number | string)[];

  barIsAnimationActive?: boolean;
  allowEscapeViewBoxX?: boolean;
}

const DIMENSIONS = {
  height: 290,
  margin: {
    top: BASE * 4,
    left: -BASE * 2,
  },
};

const BarChartWithLegend = ({
  data,
  barKeys,
  barColors,
  stackId,
  chartWidth,
  createTooltipHeader,
  legendFormatter = startCase,
  onDeepLink,
  renderTooltip,
  legendHeight,
  barKeyFormatter,
  hideLegend,
  dataKeys,
  // x-axis
  xFormatter,
  xDataKey,
  xAxisHeight = 30,
  xAxisTextAnchor = 'middle', // passing undefined textAnchor to XAxis causes the behavior to change from the default ('middle') to 'start'
  xAxisAngle,
  xAxisTick,
  renderBarLabel,
  // y-axis
  yAxisUnit,
  yAxisTicks,
  barIsAnimationActive,
  chartHeight,
  allowEscapeViewBoxX = true,
}: BarChartWithLegendProps) => {
  const theme = useContext(ThemeContext);

  const [filteredLegendKeys, setFilteredLegendKeys] = useState<string[]>([]);
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const [legendHovered, { enable: setLegendHovered, disable: clearLegendHovered }] = useToggle();

  const handleLegendClick = useCallback(
    ({ dataKey }: Payload) =>
      typeof dataKey === 'string' &&
      setFilteredLegendKeys(
        filteredLegendKeys.includes(dataKey)
          ? filteredLegendKeys.filter((s) => s !== dataKey)
          : filteredLegendKeys.concat(dataKey),
      ),
    [filteredLegendKeys],
  );

  const handleLegendMouseEnter = useCallback(({ dataKey }: Payload) => {
    setLegendHovered();
    typeof dataKey === 'string' && setHoveredKey(dataKey);
  }, []);

  const handleLegendMouseLeave = useCallback(() => {
    clearLegendHovered();
    setHoveredKey(null);
  }, []);

  const handleDeepLink = useCallback(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    (_: unknown, index: number) => onDeepLink(data[index], filteredLegendKeys),
    [onDeepLink, data, filteredLegendKeys],
  );

  const tooltipContent = renderTooltip ? (
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    () => renderTooltip(hoveredKey)
  ) : (
    <TooltipContent
      createHeader={createTooltipHeader}
      hoveredKey={hoveredKey}
      legendFormatter={legendFormatter}
    />
  );

  const effectiveChartHeight = chartHeight ?? DIMENSIONS.height;
  const totalHeight = effectiveChartHeight + xAxisHeight;

  // When we legendHeight is undefined the ChartContentLegend is automatically sized
  const effectiveLegendHeight =
    legendHeight === 'auto' ? undefined : (legendHeight ?? effectiveChartHeight);
  return (
    <ChartLayout role='figure'>
      <ResponsiveContainer height={totalHeight} width={chartWidth || '100%'}>
        <BarChart data={data} margin={DIMENSIONS.margin}>
          <CartesianGrid vertical={false} stroke={theme.color.border.secondary} />
          <XAxis
            dataKey={xDataKey}
            axisLine={{ stroke: 'none' }}
            tickLine={{ stroke: 'none' }}
            tickFormatter={xFormatter}
            tick={
              xAxisTick ?? {
                fontSize: theme.text.paragraph.mdBold.fontSize,
                fill: theme.color.text.tertiary,
              }
            }
            interval={0}
            angle={xAxisAngle}
            textAnchor={xAxisTextAnchor}
            height={xAxisHeight}
          />
          <YAxis
            unit={yAxisUnit}
            axisLine={{ stroke: 'none' }}
            tickLine={{ stroke: 'none' }}
            ticks={yAxisTicks}
            tickFormatter={formatNumberLarge}
            tick={{
              fontSize: theme.text.paragraph.mdBold.fontSize,
              fill: theme.color.text.tertiary,
            }}
            allowDecimals={false}
          />
          <Tooltip
            isAnimationActive={false}
            content={tooltipContent}
            allowEscapeViewBox={{
              x: allowEscapeViewBoxX,
            }}
          />
          {hideLegend === true ? null : (
            <Legend
              layout='vertical'
              verticalAlign='top'
              align='right'
              formatter={legendFormatter}
              iconType='circle'
              inactiveColor={Color.CoreSlate50}
              // eslint-disable-next-line rulesdir/jsx-click-tracking-ids -- this element doesn't take ID
              onClick={handleLegendClick}
              onMouseEnter={handleLegendMouseEnter}
              onMouseLeave={handleLegendMouseLeave}
              content={<ChartLegendContent height={effectiveLegendHeight} />}
            />
          )}
          {barKeys.map((key, idx) => (
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            <Bar
              key={key}
              name={barKeyFormatter == null ? null : barKeyFormatter(key)}
              isAnimationActive={barIsAnimationActive != null ? barIsAnimationActive : true}
              dataKey={dataKeys == null ? key : dataKeys[idx]}
              stackId={stackId}
              // This element doesn't take ID and the library types are wrong
              // eslint-disable-next-line rulesdir/jsx-click-tracking-ids, @typescript-eslint/no-explicit-any
              onClick={onDeepLink ? handleDeepLink : null}
              onMouseEnter={() => setHoveredKey(key)}
              onMouseLeave={() => setHoveredKey(null)}
              fill={barColors[idx % barColors.length]}
              fillOpacity={!legendHovered || hoveredKey == null || hoveredKey === key ? 1 : 0.3}
              stroke={legendHovered && hoveredKey === key ? theme.color.border.active : undefined}
              hide={filteredLegendKeys.length > 0 && !filteredLegendKeys.includes(key)}
              style={{
                transition: `fill-opacity ${TransitionDuration.Fast}, stroke ${TransitionDuration.Fast}`,
              }}
            >
              {renderBarLabel != null && (
                <LabelList
                  content={(props) => renderBarLabel(props as RenderLabelProps, idx)}
                  position='center'
                />
              )}
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    </ChartLayout>
  );
};

export default memo(BarChartWithLegend);

/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
const TooltipContent: FC<
  ComponentProps<typeof Tooltip> & {
    hoveredKey: string | null;
    createHeader: (label: string) => string;
    legendFormatter: (value: string) => string;
  }
> = ({ active, payload, label, hoveredKey, legendFormatter, createHeader }) => {
  if (!active || payload == null) {
    return null;
  }

  const total = sumBy('value', payload);
  // reverse order to make items come in same order as chart bars
  const countLabels = payload
    .slice()
    .reverse()
    .map(({ color, dataKey, value }) =>
      typeof value === 'number' && value > 0 ? (
        <ChartTooltipLabelContainer
          key={dataKey}
          active={hoveredKey === dataKey}
          hasActive={hoveredKey != null}
        >
          <ChartTooltipLabel>
            <Dot style={{ color }} /> {legendFormatter(String(dataKey))}
          </ChartTooltipLabel>
          <ChartTooltipLabel>{formatNumber(+value)}</ChartTooltipLabel>
          <ChartTooltipLabel>
            {formatNumber(+value / total, { style: 'percent' })}
          </ChartTooltipLabel>
        </ChartTooltipLabelContainer>
      ) : null,
    )
    .filter(Boolean);

  return (
    countLabels.length > 0 && (
      <ChartTooltipContainer>
        <ChartTooltipHeader>{createHeader(label)}</ChartTooltipHeader>
        {countLabels}
        <ChartTooltipDivider />
        <ChartTooltipLabelContainer>
          <ChartTooltipLabel>Total</ChartTooltipLabel>
          <ChartTooltipLabel>{formatNumber(total)}</ChartTooltipLabel>
        </ChartTooltipLabelContainer>
      </ChartTooltipContainer>
    )
  );
};
